import React, { FormEvent, useState } from "react";

import moment, { Moment } from "moment";
import { Box } from "styles/globalComponents";

import { useBudgetCombos } from "Hooks/useBudgetCombos";

import { Button } from "Components/Button";
import InputDateRange from "Components/Form/InputDateRange";
import Select from "Components/Form/Select";
import Input from "Components/Form/Input";

import { useIsFetching, useQuery } from "react-query";
import { getUnitsUser } from "Requests/HomeOrder";
import SelectSearch from "Components/Form/SelectSearch";
import { Multiples } from "Requests/Partners/PreBudgets";
import { FiltersProps } from "./interface";
import { StyledForm } from "./styles";

const ITEMS_PER_PAGE = [
  {
    name: "10 itens por página",
    id: "10",
  },
  {
    name: "50 itens por página",
    id: "50",
  },
  {
    name: "100 itens por página",
    id: "100",
  },
];

const ORDER_BY = [
  {
    id: "desc-created_at",
    name: "Data mais recente",
  },
  {
    id: "asc-created_at",
    name: "Data mais antigo",
  },
  {
    id: "desc-id",
    name: "Número orçamento (maior)",
  },
  {
    id: "asc-id",
    name: "Número orçamento (menor)",
  },
];

const initialFilters = {
  search: "",
  start_date: undefined,
  end_date: undefined,
  page_size: "10",
  status_id: "",
  client_id: "",
  page: 1,
  order_by: "",
  units_id: null,
};

export function Filter({ searchBudgets, productsQuantity }: FiltersProps) {
  const [filters, setFilters] = useState<{
    search: string;
    start_date: Moment | undefined;
    end_date: Moment | undefined;
    page_size: string;
    status_id: string;
    client_id: string;
    page: number;
    order_by: string;
    units_id: null | Multiples;
  }>(initialFilters);

  const { data: budgetCombos, isLoading: loadingCombos } = useBudgetCombos();

  const isFetching = useIsFetching({ queryKey: ["clients-budgets-units"] }) > 0;

  const { status, clients } = budgetCombos ?? {};

  const units = useQuery({
    queryFn: getUnitsUser,
    queryKey: "user-units",
    refetchOnWindowFocus: false,
  });

  const handleSubmitSearch = (event: FormEvent) => {
    event.preventDefault();

    const filtersParam = {
      ...filters,
      start_date: filters.start_date?.format("YYYY-MM-DD") || "",
      end_date: filters.end_date?.format("YYYY-MM-DD") || "",
    };
    searchBudgets(filtersParam);
  };

  const clearFilters = () => {
    searchBudgets(initialFilters);
    setFilters(initialFilters);
  };

  const handleDatesChange = (value: any) => {
    if (value.startDate) {
      setFilters((prev) => ({
        ...prev,
        start_date: value.startDate,
      }));
    }

    if (value.endDate) {
      setFilters((prev) => ({
        ...prev,
        end_date: value.endDate,
      }));
    }
  };

  const onSelectPrevDay = () => {
    const yesterdayDate = moment().subtract(1, "day");
    const tomorrowDate = moment();

    const currentFiterSate = {
      ...filters,
      search: "",
      start_date: yesterdayDate,
      end_date: tomorrowDate,
      status: "",
      partner: "",
      page: 1,
      order_by: "",
    };

    setFilters(currentFiterSate);

    searchBudgets({
      ...currentFiterSate,
      start_date: yesterdayDate.format("YYYY-MM-DD"),
      end_date: tomorrowDate.format("YYYY-MM-DD"),
    });
  };

  return (
    <StyledForm className="mb-1">
      <div className="row">
        <div className="lg-2 mt-1">
          <Input
            placeholder="Busque pelo nome ou documento do paciente"
            id="search"
            type="text"
            value={filters.search}
            onChange={({ target }: any) =>
              setFilters((prev) => ({ ...prev, search: target.value }))
            }
            error=""
            description=""
            label=""
            readOnly={false}
            mask=""
            maskChar=""
            maxLength={200}
            min={1}
            multiline={false}
            onBlur={() => {}}
            onFocus={() => {}}
            onKeyPress={() => {}}
            purple={false}
            required={false}
            small={false}
          />
        </div>

        <div className="lg-2 mt-1">
          <InputDateRange
            startDatePlaceholderText="Data inicial"
            endDatePlaceholderText="Data final"
            startDateId="start"
            endDateId="end"
            onDatesChange={(value: any) => handleDatesChange(value)}
            openInModal={false}
            dates={{
              startDate: filters.start_date,
              endDate: filters.end_date,
            }}
            blockPastChanges={false}
            withClear={false}
            error={undefined}
            small={undefined}
            purple={undefined}
            label={undefined}
          />
        </div>
        <div className="lg-1 mt-1">
          <Select
            placeholder="Parceiro"
            id="clients_id"
            options={clients}
            value={filters.client_id}
            readOnly={loadingCombos}
            optionName="Parceiro"
            onChange={({ target: { value } }: any) =>
              setFilters((prev) => ({ ...prev, client_id: value }))
            }
          />
        </div>

        <div className="lg-1 mt-1">
          <SelectSearch
            id="units_id"
            label=""
            value={filters.units_id}
            error=""
            isMulti
            isMoreSelect
            options={units?.data}
            loading={!units?.data?.length}
            readOnly={!units?.data?.length}
            valueKey="id"
            labelKey="name"
            placeholder="Unidades"
            onChange={(value: any) => {
              if (value.id === "")
                return setFilters((prev) => ({ ...prev, units_id: null }));
              setFilters((prev) => ({ ...prev, units_id: value }));
            }}
          />
        </div>

        <div className="lg-1 mt-1">
          <Select
            placeholder="Status"
            id="status_id"
            value={filters.status_id}
            options={status}
            readOnly={loadingCombos}
            optionName="status"
            onChange={({ target: { value } }: any) =>
              setFilters((prev) => ({ ...prev, status_id: value }))
            }
          />
        </div>

        <div className="lg-1 mt-1">
          <Select
            id="per_page"
            placeholder="Selecione"
            optionName="name"
            options={ITEMS_PER_PAGE}
            value={filters.page_size}
            onChange={({ target: { value } }: any) =>
              setFilters((prev) => ({ ...prev, page_size: value }))
            }
          />
        </div>

        <Box
          className="buttons-container"
          display="flex"
          mt="16"
          ml="20"
          gap="10"
        >
          <Button onClick={onSelectPrevDay}> D-1</Button>

          <Button
            type="button"
            onClick={clearFilters}
            outlined
            borderRadius="111"
          >
            Limpar
          </Button>
          <Button borderRadius="111" onClick={handleSubmitSearch} type="submit">
            Filtrar
          </Button>
        </Box>

        <div className="lg-2 mt-1">
          <Select
            placeholder="Order por"
            id="order_by"
            options={ORDER_BY}
            value={filters.order_by}
            readOnly={loadingCombos}
            optionName="status"
            onChange={({ target: { value } }: any) =>
              setFilters((prev) => ({ ...prev, order_by: value }))
            }
          />
        </div>
      </div>
      {isFetching ? null : (
        <Box mt="20" display="flex" justify="end">
          <p>{productsQuantity} resultado(s) encontrado(s)</p>
        </Box>
      )}
    </StyledForm>
  );
}
